import React from "react"
import { useState } from "react"
import { NavLink, Outlet, useLocation } from "react-router"
import { Dialog, DialogBackdrop, DialogPanel, TransitionChild } from "@headlessui/react"
import { Bars3Icon, BellIcon, CalendarDaysIcon, CalendarIcon, ChatBubbleLeftRightIcon, ChartPieIcon, DocumentDuplicateIcon, FolderIcon, HomeIcon, TableCellsIcon, XMarkIcon } from "@heroicons/react/24/outline"
import EnkLogo from "../components/enk-logo"
import EnkIwpLogo from "../components/iwp-logo"
import { useAuth } from "../auth/use-auth"
import { useMsgDialog } from "../dialogs/use-msgdialog"
import { classNames } from "../utils/styling-utils"
import { EnkUserProfileButton } from "../components/user-controls/user-profile"
import { EnkGroupSelectionButton } from "../components/user-controls/group-selection"

interface IEnkNavItem {
  id: number
  name: string
  link: string
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>
}

const navigation: IEnkNavItem[] = [
  { id: 10, name: "Dashboard", link: "/dashboard", icon: HomeIcon },
  { id: 20, name: "Installaties", link: "/installaties/all", icon: FolderIcon },
  { id: 30, name: "Projecten", link: "/werken/all", icon: FolderIcon },
  { id: 70, name: "Agenda", link: "/calendar", icon: CalendarIcon },
  { id: 80, name: "Documenten", link: "/documents", icon: DocumentDuplicateIcon },
  { id: 90, name: "Rapporten", link: "/reports", icon: ChartPieIcon },
  { id: 40, name: "Meldingen", link: "/meldingen", icon: ChatBubbleLeftRightIcon },
  { id: 100, name: "Dataverkenner", link: "/dataexplorer", icon: TableCellsIcon },
  { id: 110, name: "Planner", link: "/planner", icon: CalendarDaysIcon },
]

//const settingItem: IEnkNavItem = { id: 999, name: "Instellingen", link: "/settings", icon: Cog6ToothIcon }

interface EnkNavLinkProps {
  item: IEnkNavItem
  onClick?: () => void
}

export function EnkNavLink({ item, onClick }: EnkNavLinkProps) {
  return (
    <NavLink
      to={item.link}
      onClick={onClick}
      className={({ isActive }) =>
        classNames(
          isActive ? "bg-gray-100 text-primary" : "text-gray-700 hover:bg-gray-50 hover:text-primary",
          "group flex gap-x-3 rounded-md p-2 text-sm font-semibold leading-6"
        )
      }
    >
      {({ isActive }) => (
        <>
          <item.icon
            aria-hidden="true"
            className={classNames(isActive ? "text-primary" : "text-gray-400 group-hover:text-primary", "h-6 w-6 shrink-0")}
          />
          {item.name}
        </>
      )}
    </NavLink>
  )
}

export default function EnkLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const { user, onLogout } = useAuth()
  const { messageDialog } = useMsgDialog()
  const location = useLocation() // Get the current location

  // Function to get the current page title based on the URL
  const getPageTitle = () => {
    const currentPath = location.pathname // Get the current path

    // Check if the current path matches a report page
    if (currentPath.startsWith("/report/")) {
      const reportName = currentPath.split("/report/")[1] || "Unknown Report" // Extract the report name from the path
      return `Rapport - ${decodeURIComponent(reportName)}` // Decode in case of URL-encoded characters
    }

    if (currentPath === "/meldingen") {
      return "Meldingen"
    }

    if (currentPath.startsWith("/meldingen/")) {
      return "Melding details"
    }

    //Check for settings page
    if (currentPath === "/notifications") {
      return "Notificaties"
    }

    // Check for user profile page
    if (currentPath === "/profile") {
      return "Mijn gegevens"
    }

    const matchedItem = navigation.find((item) => item.link === currentPath) // Match the path with navigation items
    return matchedItem?.name || "Dashboard" // Default to "Dashboard" if no match is found
  }

  const handleLogout = () => {
    messageDialog("confirmation", "Weet je zeker dat je wilt afmelden?", ["Afmelden", "Annuleren"], (button) => {
      if (button === "Afmelden") {
        onLogout()
      }
    })
  }

  const userHasAccess = (id: number) => {
    //test if given id is present in user.canUseModules if so return true otherwise return false
    return user?.canUseModules.includes(id) || false
  }

  return (
    <>
      <Dialog open={sidebarOpen} onClose={setSidebarOpen} className="relative z-50 lg:hidden">
        <DialogBackdrop transition className="fixed inset-0 bg-gray-900/80 transition-opacity duration-300 ease-linear data-closed:opacity-0" />

        <div className="fixed inset-0 flex">
          <DialogPanel
            transition
            className="relative mr-16 flex w-full max-w-xs flex-1 transform transition duration-300 ease-in-out data-closed:-translate-x-full"
          >
            <TransitionChild>
              <div className="absolute left-full top-0 flex w-16 justify-center pt-5 duration-300 ease-in-out data-closed:opacity-0">
                <button type="button" onClick={() => setSidebarOpen(false)} className="-m-2.5 p-2.5">
                  <span className="sr-only">Close sidebar</span>
                  <XMarkIcon aria-hidden="true" className="h-6 w-6 text-white" />
                </button>
              </div>
            </TransitionChild>
            {/* Sidebar component, swap this element with another sidebar if you like */}
            <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-white px-6 pb-4">
              <div className="flex w-full h-16 shrink-0 items-center justify-center">
                <EnkIwpLogo />
              </div>
              <nav className="flex flex-1 flex-col">
                <ul role="list" className="flex flex-1 flex-col gap-y-7">
                  <li>
                    <ul role="list" className="-mx-2 space-y-1">
                      {navigation.map(
                        (item) =>
                          userHasAccess(item.id) && (
                            <li key={item.name}>
                              <EnkNavLink item={item} onClick={() => setSidebarOpen(false)} />
                            </li>
                          )
                      )}
                    </ul>
                  </li>

                  {/* <li className="mt-auto">
                    <EnkNavLink item={settingItem} onClick={() => setSidebarOpen(false)} />
                  </li> */}
                </ul>
              </nav>
            </div>
          </DialogPanel>
        </div>
      </Dialog>
      <div className="h-full">
        {/* Static sidebar for desktop */}
        <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-72 lg:flex-col">
          {/* Sidebar component */}
          <div className="flex grow flex-col gap-y-5 overflow-y-auto border-r border-gray-200 bg-white px-6 pb-4">
            <div className="flex w-full h-16 shrink-0 items-center justify-center">
              <EnkIwpLogo />
            </div>
            <nav className="flex flex-1 flex-col">
              <ul role="list" className="flex flex-1 flex-col gap-y-7">
                <li>
                  <ul role="list" className="-mx-2 space-y-1">
                    {navigation.map(
                      (item) =>
                        userHasAccess(item.id) && (
                          <li key={item.name}>
                            <EnkNavLink item={item} />
                          </li>
                        )
                    )}
                  </ul>
                </li>

                {/* <li className="mt-auto">
                  <EnkNavLink item={settingItem} />
                </li> */}
              </ul>
            </nav>
            <div className="flex w-full h-12 shrink-0 items-center justify-center border-t border-gray-200 pt-2">
              <EnkLogo />
            </div>
          </div>
        </div>

        {/* content panel (topbar and pagecontent) */}
        <div className="lg:pl-72">
          {/* Top bar */}
          <div className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 border-b border-gray-200 bg-white px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:px-8">
            {/* hamburger menu button */}
            <button type="button" onClick={() => setSidebarOpen(true)} className="-m-2.5 p-2.5 text-gray-700 lg:hidden">
              <span className="sr-only">Open sidebar</span>
              <Bars3Icon aria-hidden="true" className="h-6 w-6" />
            </button>

            {/* Separator */}
            <div aria-hidden="true" className="h-6 w-px bg-gray-200 lg:hidden" />

            <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
              {/* page title - breadcrum */}
              <div className="flex items-center gap-x-2 lg:gap-x-4 grow">
                <h1 className="text-base font-semibold text-gray-900">{getPageTitle()}</h1>
              </div>

              <div className="flex items-center gap-x-4 lg:gap-x-6">
                <button type="button" className="-m-2.5 p-2.5 text-gray-400 hover:text-gray-500">
                  <span className="sr-only">View notifications</span>
                  <BellIcon aria-hidden="true" className="h-6 w-6" />
                </button>

                {/* Separator */}
                <div aria-hidden="true" className="hidden lg:block lg:h-6 lg:w-px lg:bg-gray-200" />

                {/* Group selection dropdown */}
                <EnkGroupSelectionButton user={user} />

                {/* Profile dropdown */}
                <EnkUserProfileButton user={user} handleLogout={handleLogout} />
              </div>
            </div>
          </div>

          {/* page content inner div had:  */}
          <main className="h-full">
            <Outlet />
          </main>
        </div>
      </div>
    </>
  )
}
